// ─────────────────────────────────────────────────────────────────────────────
// PreferredProductService
// ─────────────────────────────────────────────────────────────────────────────
// HTTP service for preferred product operations.
// Endpoints: /ai/kb/preferred-products for all preferred product operations.
// Manual + upload rows are editable (purchase frequency only); order-sourced
// entries remain read-only on the backend.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { skipErrorToast } from 'src/app/core/http/error-toast.token';
import { APIResponse } from 'src/app/core/models/api-response';
import { IPageCount } from 'src/app/shared/models/page.model';
import { environment } from 'src/environments/environment';
import { IImportPreviewResponse } from '../models/import.models';
import {
  ICreatePreferredProductPayload,
  IPreferredProduct,
  IUpdatePreferredProductPayload,
  PreferredProductFilters,
} from '../models/preferred-product.models';

@Injectable({
  providedIn: 'root',
})
export class PreferredProductService {
  private readonly baseUrl = `${environment.BASE_URL_API}ai/kb/preferred-products`;

  constructor(private http: HttpClient) {}

  // ── List ───────────────────────────────────────────────────────────────────

  public listPreferredProducts(
    page: IPageCount,
    key?: string,
    filters?: PreferredProductFilters,
    sortBy?: string,
    sortMode?: 'asc' | 'desc',
  ): Observable<APIResponse<IPreferredProduct>> {
    let params = new HttpParams().set('page', page.number).set('limit', page.size);

    if (key) {
      params = params.set('key', key);
    }
    if (sortBy) {
      params = params.set('sort_by', sortBy);
    }
    if (sortMode) {
      params = params.set('sort_mode', sortMode);
    }
    params = this.appendFilterParams(params, filters);

    return this.http.get<APIResponse<IPreferredProduct>>(this.baseUrl, { params });
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  public createPreferredProduct(payload: ICreatePreferredProductPayload): Observable<APIResponse<IPreferredProduct>> {
    return this.http.post<APIResponse<IPreferredProduct>>(this.baseUrl, payload);
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  public updatePreferredProduct(id: number, payload: IUpdatePreferredProductPayload): Observable<APIResponse<IPreferredProduct>> {
    return this.http.post<APIResponse<IPreferredProduct>>(`${this.baseUrl}/${id}`, payload);
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  public deletePreferredProduct(id: number): Observable<APIResponse<IPreferredProduct>> {
    return this.http.delete<APIResponse<IPreferredProduct>>(`${this.baseUrl}/${id}`);
  }

  // ── Bulk delete ────────────────────────────────────────────────────────────

  public bulkDeletePreferredProducts(ids: number[]): Observable<APIResponse<IPreferredProduct>> {
    return this.http.post<APIResponse<IPreferredProduct>>(`${this.baseUrl}/bulk/delete`, { ids });
  }

  // ── Export ──────────────────────────────────────────────────────────────────

  public exportPreferredProducts(
    format: 'csv' | 'xlsx' = 'xlsx',
    key?: string,
    filters?: PreferredProductFilters,
    sortBy?: string,
    sortMode?: 'asc' | 'desc',
  ): Observable<Blob> {
    let params = new HttpParams().set('format', format);

    if (key) params = params.set('key', key);
    if (sortBy) params = params.set('sort_by', sortBy);
    if (sortMode) params = params.set('sort_mode', sortMode);
    params = this.appendFilterParams(params, filters);

    return this.http.get(`${this.baseUrl}/export`, {
      params,
      responseType: 'blob',
      // Blob responses can't carry a parseable error body, so the global
      // interceptor would only ever show a generic toast — the component
      // surfaces a specific "export failed" message instead.
      context: skipErrorToast(),
    });
  }

  // ── Import ─────────────────────────────────────────────────────────────────

  /**
   * Upload a CSV file for preferred product import.
   * @param file FormData with field name 'ufile'
   * @param dryRun If true, validates without persisting (preview mode)
   */
  public importPreferredProducts(file: FormData, dryRun = false): Observable<{ object: IImportPreviewResponse }> {
    const url = dryRun ? `${this.baseUrl}/import?dry_run=1` : `${this.baseUrl}/import`;
    return this.http.post<{ object: IImportPreviewResponse }>(url, file);
  }

  public getCsvTemplateUrl(): string {
    return `${this.baseUrl}/import/template?format=csv`;
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  private appendFilterParams(params: HttpParams, filters?: PreferredProductFilters): HttpParams {
    if (!filters) return params;
    if (filters.customer_id != null) params = params.set('customer_id', filters.customer_id);
    if (filters.source) params = params.set('source', filters.source);
    if (filters.create_date_from) params = params.set('create_date_from', filters.create_date_from);
    if (filters.create_date_to) params = params.set('create_date_to', filters.create_date_to);
    return params;
  }
}
